import React, { useState, useEffect } from 'react';
import { FaClock, FaCheckCircle, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';

const ProcedureTimer = ({ setStep, procedureData, setProcedureData, onComplete }) => {
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [isRunning, setIsRunning] = useState(true);
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get start time from procedureData or use current time
  const startTime = procedureData?.startTime ? new Date(procedureData.startTime).getTime() : Date.now();

  // Update timer every second
  useEffect(() => {
    let interval;
    
    if (isRunning) {
      interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning]);

  // Format elapsed time
  const formatElapsedTime = (elapsed) => {
    const totalSeconds = Math.floor(elapsed / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleCompleteClick = () => {
    setShowConfirmComplete(true);
  };

  const handleDiscardClick = () => {
    setShowConfirmDiscard(true);
  };

  const handleConfirmComplete = async () => {
    setIsRunning(false);
    setIsSaving(true);
    
    try {
      const endTime = new Date();
      const duration = endTime.getTime() - startTime;
      const durationSeconds = Math.floor(duration / 1000);
      
      // Update procedure data with completion info
      const completedData = {
        ...procedureData,
        endTime: endTime.toISOString(),
        duration: durationSeconds,
        durationFormatted: formatElapsedTime(duration),
        status: 'completed',
        completed: true
      };

      // Call onComplete callback to save to Firebase
      if (onComplete) {
        await onComplete(completedData);
      }
      
      setShowConfirmComplete(false);
    } catch (error) {
      console.error('Error completing procedure:', error);
      setIsSaving(false);
      setIsRunning(true);
    }
  };

  const handleConfirmDiscard = () => {
    setIsRunning(false);
    // Discard without saving - no Firebase record created
    setShowConfirmDiscard(false);
    setStep('mainScreen');
  };

  const handleCancelComplete = () => {
    setShowConfirmComplete(false);
  };

  const handleCancelDiscard = () => {
    setShowConfirmDiscard(false);
  };

  const elapsedTime = currentTime - startTime;

  // Get procedure type display name
  const getProcedureTypeName = () => {
    const procedureNames = {
      'extraction': 'Tooth Extraction',
      'implant': 'Dental Implant',
      'biopsy': 'Oral Biopsy',
      'cyst': 'Cyst Removal',
      'fracture': 'Fracture Repair',
      'orthognathic': 'Orthognathic Surgery',
      'tmj': 'TMJ Surgery',
      'reconstruction': 'Facial Reconstruction',
      'other': 'Other Procedure'
    };
    return procedureNames[procedureData?.procedureType] || 'Procedure';
  };

  return (
    <div className="min-h-screen bg-[#f3f7fb] py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl ring-1 ring-gray-900/5 overflow-hidden">
          {/* Header Section */}
          <div className="bg-[#103a74] px-8 py-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold tracking-tight text-white mb-2">
                Procedure In Progress
              </h1>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm text-blue-100">
                <span className="font-medium">{getProcedureTypeName()}</span>
                <span className="hidden sm:inline">•</span>
                <span>Patient ID: <span className="font-semibold text-white">{procedureData?.patientID || 'N/A'}</span></span>
                <span className="hidden sm:inline">•</span>
                <span className="text-xs text-blue-200">ID: {procedureData?.procedureId || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Timer Display Section */}
          <div className="px-8 py-16 bg-[#f3f7fb]">
            <div className="flex flex-col items-center justify-center">
              <div className="mb-8 flex items-center justify-center">
                <div className="relative">
                  <div className="relative flex size-20 items-center justify-center rounded-full bg-[#103a74] shadow-lg ring-4 ring-blue-50">
                    <FaClock className="size-10 text-white" />
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">
                  Elapsed Time
                </div>
                <div className="text-7xl font-bold text-gray-900 font-mono tracking-tight">
                  {formatElapsedTime(elapsedTime)}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons Section */}
          <div className="px-8 py-8 bg-white border-t border-gray-200">
            {!showConfirmComplete && !showConfirmDiscard && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                <button
                  onClick={handleDiscardClick}
                  className="group inline-flex items-center justify-center gap-3 rounded-xl bg-white px-8 py-4 text-base font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                >
                  <FaTimes className="size-5 text-gray-400 group-hover:text-red-500 transition-colors" />
                  Discard
                </button>
                <button
                  onClick={handleCompleteClick}
                  className="group inline-flex items-center justify-center gap-3 rounded-xl bg-[#103a74] px-8 py-4 text-base font-semibold text-white shadow-lg hover:bg-[#0d2d5c] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#103a74]"
                >
                  <FaCheckCircle className="size-5 group-hover:scale-110 transition-transform" />
                  Complete Procedure
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Complete Confirmation Dialog */}
        <Dialog open={showConfirmComplete} onClose={handleCancelComplete} className="relative z-50">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl ring-1 ring-black/5 transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <div className="bg-[#f3f7fb] px-6 pt-6 pb-4">
                  <div className="flex items-start">
                    <div className="mx-auto flex size-14 shrink-0 items-center justify-center rounded-full bg-[#103a74] shadow-lg ring-4 ring-blue-50 sm:mx-0">
                      <FaCheckCircle className="size-7 text-white" aria-hidden="true" />
                    </div>
                    <div className="ml-4 flex-1 text-left">
                      <h3 className="text-xl font-bold text-gray-900">
                        Complete Procedure
                      </h3>
                      <div className="mt-3">
                        <p className="text-sm text-gray-600">
                          Are you sure you want to complete this procedure? The procedure record will be saved to the database.
                        </p>
                        <div className="mt-3 rounded-lg bg-white px-4 py-2.5 ring-1 ring-gray-200">
                          <p className="text-sm font-semibold text-gray-700">
                            Duration: <span className="text-base font-mono text-gray-900">{formatElapsedTime(elapsedTime)}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-6 py-4 sm:flex sm:flex-row-reverse sm:gap-3">
                  <button
                    type="button"
                    onClick={handleConfirmComplete}
                    disabled={isSaving}
                    className="inline-flex w-full justify-center items-center gap-2 rounded-xl bg-[#103a74] px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-[#0d2d5c] hover:shadow-xl transition-all duration-200 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#103a74] disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="size-4" />
                        Yes, Complete
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelComplete}
                    disabled={isSaving}
                    className="mt-3 inline-flex w-full justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:shadow-md transition-all duration-200 outline-none sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>

        {/* Discard Confirmation Dialog */}
        <Dialog open={showConfirmDiscard} onClose={handleCancelDiscard} className="relative z-50">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl ring-1 ring-black/5 transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <div className="bg-[#f3f7fb] px-6 pt-6 pb-4">
                  <div className="flex items-start">
                    <div className="mx-auto flex size-14 shrink-0 items-center justify-center rounded-full bg-[#103a74] shadow-lg ring-4 ring-blue-50 sm:mx-0">
                      <FaExclamationTriangle className="size-7 text-white" aria-hidden="true" />
                    </div>
                    <div className="ml-4 flex-1 text-left">
                      <h3 className="text-xl font-bold text-gray-900">
                        Discard Procedure
                      </h3>
                      <div className="mt-3">
                        <p className="text-sm text-gray-600">
                          Are you sure you want to discard this procedure? All data will be lost and no record will be saved.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-6 py-4 sm:flex sm:flex-row-reverse sm:gap-3">
                  <button
                    type="button"
                    onClick={handleConfirmDiscard}
                    className="inline-flex w-full justify-center items-center gap-2 rounded-xl bg-[#103a74] px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-[#0d2d5c] hover:shadow-xl transition-all duration-200 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#103a74] sm:w-auto"
                  >
                    <FaTimes className="size-4" />
                    Yes, Discard
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelDiscard}
                    className="mt-3 inline-flex w-full justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:shadow-md transition-all duration-200 outline-none sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default ProcedureTimer;
